import type { Vec2, Segment, PolygonWithHoles, AABB, Ring } from '../geo/types';
import type { Channel } from '../data/types';
import type { WaterEdge, QuayKind } from './types';
import type { WaterClassifier } from './classify';
import { pointInPolygon } from '../geo/polygon';

const PROBE_DISTANCE_M = 0.5;
const MIN_EDGE_LENGTH_M = 0.01;

interface EdgeContext {
  readonly water: readonly PolygonWithHoles[];
  readonly classifier: WaterClassifier;
  readonly bounds: AABB;
  readonly town: readonly PolygonWithHoles[];
}

export function buildWaterEdges(
  water: readonly PolygonWithHoles[],
  classifier: WaterClassifier,
  bounds: AABB,
  cityEnclosure: readonly Vec2[] | undefined,
  lastage: readonly Vec2[] | undefined
): readonly WaterEdge[] {
  const town = [cityEnclosure, lastage]
    .filter((ring): ring is readonly Vec2[] => ring !== undefined && ring.length >= 3)
    .map((ring) => ({ outer: ring, holes: [] }));
  const context: EdgeContext = { water, classifier, bounds, town };

  return water.flatMap((poly) =>
    [poly.outer, ...poly.holes].flatMap((ring) => ringEdges(ring, context))
  );
}

function ringEdges(ring: Ring, context: EdgeContext): readonly WaterEdge[] {
  return ring.flatMap((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    if (!isInBounds(a, context.bounds) && !isInBounds(b, context.bounds)) return [];
    if (isAlongBounds(a, b, context.bounds)) return [];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    if (Math.sqrt(dx * dx + dy * dy) < MIN_EDGE_LENGTH_M) return [];
    return [classifyEdge({ a, b }, context)];
  });
}

function classifyEdge(segment: Segment, context: EdgeContext): WaterEdge {
  const { a, b } = segment;
  const mid: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const towardWater = towardWaterAt(segment, mid, context.water);
  const probe: Vec2 = [
    mid[0] + towardWater[0] * PROBE_DISTANCE_M,
    mid[1] + towardWater[1] * PROBE_DISTANCE_M,
  ];
  const { channel, kind } = context.classifier.classify(probe);

  return { segment, quay: quayAt(probe, channel, context.town), waterKind: kind, towardWater };
}

function towardWaterAt(segment: Segment, mid: Vec2, water: readonly PolygonWithHoles[]): Vec2 {
  const dx = segment.b[0] - segment.a[0];
  const dy = segment.b[1] - segment.a[1];
  const len = Math.sqrt(dx * dx + dy * dy);
  const nx = -dy / len;
  const ny = dx / len;
  const inWater = (p: Vec2) => water.some((w) => pointInPolygon(p, w));
  const outwardWet = inWater([mid[0] + nx * PROBE_DISTANCE_M, mid[1] + ny * PROBE_DISTANCE_M]);
  const inwardWet = inWater([mid[0] - nx * PROBE_DISTANCE_M, mid[1] - ny * PROBE_DISTANCE_M]);

  return inwardWet && !outwardWet ? [-nx, -ny] : [nx, ny];
}

// PLAN §5 step 6: the channel under the probe sets the quay; otherwise the town has wooden sheet
// piling and the countryside earth banks.
function quayAt(probe: Vec2, channel: Channel | null, town: readonly PolygonWithHoles[]): QuayKind {
  if (channel) return channel.quay;
  return town.some((polygon) => pointInPolygon(probe, polygon)) ? 'wood' : 'earth';
}

// The water is clipped to the bounds and the far ring carries it on from there, so an edge along
// the bounds is no shore: a quay on it would stand in open water.
function isAlongBounds(a: Vec2, b: Vec2, bounds: AABB): boolean {
  const onSide = (u: number, v: number, low: number, high: number) =>
    u === v && (u === low || u === high);
  return (
    onSide(a[0], b[0], bounds.minX, bounds.maxX) || onSide(a[1], b[1], bounds.minY, bounds.maxY)
  );
}

function isInBounds(p: Vec2, bounds: AABB): boolean {
  return p[0] >= bounds.minX && p[0] <= bounds.maxX && p[1] >= bounds.minY && p[1] <= bounds.maxY;
}
